import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconFileTypePdf, IconPlus, IconX } from "@tabler/icons-react";
import { cn } from "@/utils/cn";

export const FEEDBACK_MAX_SCREENSHOTS = 5;
export const FEEDBACK_MAX_SCREENSHOT_MB = 10;
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf";

interface FeedbackScreenshotZoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  onError: (message: string) => void;
  disabled?: boolean;
}

/**
 * Multi-file (images and PDFs) drop target for the feedback form. Follows the same drop/browse
 * mechanics as `TeeDropZone`, but accepts several files and renders thumbnails
 * on the dark feedback surface.
 */
export const FeedbackScreenshotZone: React.FC<FeedbackScreenshotZoneProps> = ({
  files,
  onChange,
  onError,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const previews = useMemo(
    () => files.map((file) => URL.createObjectURL(file)),
    [files],
  );
  useEffect(
    () => () => previews.forEach((url) => URL.revokeObjectURL(url)),
    [previews],
  );

  const addFiles = (candidates: FileList | null | undefined) => {
    if (!candidates || candidates.length === 0) return;
    const accepted = ACCEPT.split(",");
    const next = [...files];
    for (const candidate of Array.from(candidates)) {
      if (next.length >= FEEDBACK_MAX_SCREENSHOTS) {
        onError(
          t("feedback.error_too_many", { count: FEEDBACK_MAX_SCREENSHOTS }),
        );
        break;
      }
      if (!accepted.includes(candidate.type)) {
        onError(t("feedback.error_file_type", { name: candidate.name }));
        continue;
      }
      if (candidate.size > FEEDBACK_MAX_SCREENSHOT_MB * 1024 * 1024) {
        onError(
          t("feedback.error_file_size", {
            name: candidate.name,
            size: FEEDBACK_MAX_SCREENSHOT_MB,
          }),
        );
        continue;
      }
      next.push(candidate);
    }
    onChange(next);
  };

  const removeAt = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const openBrowser = () => {
    if (!disabled) inputRef.current?.click();
  };

  const isFull = files.length >= FEEDBACK_MAX_SCREENSHOTS;

  return (
    <div className="flex w-full flex-col gap-3">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {files.length > 0 && (
        <ul
          className="flex flex-wrap gap-3"
          aria-label={t("feedback.screenshots_label")}
        >
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}-${index}`}
              className="relative"
            >
              {file.type === "application/pdf" ? (
                <div
                  title={file.name}
                  className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-[8px] border border-[var(--text-on-forest-muted)] px-1 text-[var(--text-on-forest)]"
                >
                  <IconFileTypePdf size={28} aria-hidden="true" />
                  <span className="w-full truncate text-center text-[11px]">
                    {file.name}
                  </span>
                </div>
              ) : (
                <img
                  src={previews[index]}
                  alt={file.name}
                  className="h-20 w-20 rounded-[8px] border border-[var(--text-on-forest-muted)] object-cover"
                />
              )}
              <button
                type="button"
                aria-label={t("feedback.remove_screenshot", {
                  name: file.name,
                })}
                disabled={disabled}
                onClick={() => removeAt(index)}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--background-card)] text-[var(--color-forest)] shadow-[0_2px_6px_rgba(0,0,0,0.25)] hover:bg-[var(--background-card-hover)]"
              >
                <IconX size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!isFull && (
        <button
          type="button"
          disabled={disabled}
          onClick={openBrowser}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setIsDragOver(true);
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            if (!disabled) setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (!disabled) addFiles(e.dataTransfer.files);
          }}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-[10px] border-2 border-dashed px-4 py-5",
            "text-[16px] font-medium transition-colors",
            isDragOver
              ? "border-[var(--text-on-forest)] bg-[var(--color-forest-deep)] text-[var(--text-on-forest)]"
              : "border-[var(--text-on-forest-muted)] text-[var(--text-on-forest-muted)] hover:border-[var(--text-on-forest)] hover:text-[var(--text-on-forest)]",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          <IconPlus size={20} aria-hidden="true" />
          <span>{t("feedback.attach_screenshots")}</span>
        </button>
      )}
    </div>
  );
};

export default FeedbackScreenshotZone;
